import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogoVyrobce } from "../pages/Polozky";
import { useAuth } from "../context/AuthContext";
import { canSee } from "../lib/nav";
import api from "../lib/api";
import Vyber from "./Vyber";

/**
 * Výrobce a model se vybírají jen z ceníku. Co v ceníku není, se do něj
 * musí nejdřív doplnit — obchod dostane odkaz, technik prosbu, ať to
 * obchod doplní. Data bere z `/api/polozky/vyber-zarizeni` (bez cen).
 */
export function useCenikVyber() {
  const [data, setData] = useState({ vyrobci: [], zarizeni: [], nacteno: false });
  useEffect(() => {
    api.get("/api/polozky/vyber-zarizeni")
      .then((r) => setData({ vyrobci: r.data.vyrobci || [], zarizeni: r.data.zarizeni || [], nacteno: true }))
      .catch(() => setData((d) => ({ ...d, nacteno: true })));
  }, []);
  return data;
}

/** Odkaz do ceníku pro obchod; technik ceník neupravuje. */
export function DoplnitDoCeniku({ co = "výrobce" }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const smi = canSee(user, ["OWNER", "OBCHODNIK"]);
  return smi ? (
    <button type="button" onClick={() => nav("/polozky")} className="t-meta font-600 text-accent hover:underline"
      data-testid="doplnit-do-ceniku">Doplnit {co} do ceníku →</button>
  ) : (
    <span className="t-meta text-muted">Chybí v ceníku — požádej obchod, ať {co} doplní.</span>
  );
}

/**
 * Chipy výrobců. `kategorieId` zúží na výrobce, kteří mají v ceníku něco
 * pro danou technologii (když nikdo, ukáže všechny). `volnyText` povolí
 * „Jiný…" — jen u technologie v obchodu, kde výrobce ještě nemusí být jasný.
 */
export function VyberVyrobce({ vyrobci, kategorieId, vyrobceId, vyrobce = "", jiny = false, volnyText = false, onChange, testid = "vyrobce" }) {
  const proKategorii = kategorieId ? vyrobci.filter((v) => (v.kategorie_ids || []).includes(kategorieId)) : vyrobci;
  const nabidka = proKategorii.length ? proKategorii : vyrobci;
  const vsichni = kategorieId && !proKategorii.length && vyrobci.length > 0;
  const chip = "tap flex items-center gap-2 pl-1.5 pr-3 rounded-full border t-telo font-600 transition";
  return (
    <div className="space-y-2" data-testid={`${testid}-vyber`}>
      {vsichni && <div className="t-meta text-muted">Pro tuto technologii zatím v ceníku žádný výrobce není — vyber z ostatních.</div>}
      <div className="flex flex-wrap gap-2">
        {nabidka.map((v) => {
          const aktivni = !jiny && vyrobceId === v.id;
          return (
            <button key={v.id} type="button" onClick={() => onChange({ vyrobce_id: v.id, vyrobce: v.name, jiny: false })}
              data-testid={`${testid}-${v.id}`}
              className={`${chip} ${aktivni ? "border-ink bg-ink text-white" : "border-line-2 bg-white text-ink hover:border-ink"}`}>
              <LogoVyrobce v={v} size={28} />
              <span>{v.name}</span>
            </button>
          );
        })}
        {volnyText && (
          <button type="button" onClick={() => onChange({ vyrobce_id: null, vyrobce: jiny ? vyrobce : "", jiny: true })}
            data-testid={`${testid}-jiny`}
            className={`${chip} pl-3 ${jiny ? "border-ink bg-ink text-white" : "border-dashed border-line-2 bg-white text-sec hover:border-ink"}`}>
            {vyrobci.length ? "Jiný…" : "Zadat ručně"}
          </button>
        )}
      </div>
      {volnyText && jiny && (
        <input className="field" value={vyrobce} autoFocus onChange={(e) => onChange({ vyrobce_id: null, vyrobce: e.target.value, jiny: true })}
          data-testid={`${testid}-text`} placeholder="Např. Zehnder" />
      )}
      {!volnyText && <DoplnitDoCeniku co="výrobce" />}
    </div>
  );
}

/** Model = zařízení z ceníku (priorita A) vybraného výrobce, případně jen pro danou technologii. */
export function VyberModel({ zarizeni, vyrobceId, kategorieId, polozkaId, onChange, testid = "model" }) {
  const seznam = zarizeni.filter((z) => z.vyrobce_id === vyrobceId && (!kategorieId || !z.kategorie_id || z.kategorie_id === kategorieId));
  if (!vyrobceId) return <div className="t-meta text-muted">Nejdřív vyber výrobce.</div>;
  if (!seznam.length) {
    return (
      <div className="space-y-1">
        <div className="t-meta text-muted">Tento výrobce nemá v ceníku žádné zařízení.</div>
        <DoplnitDoCeniku co="zařízení" />
      </div>
    );
  }
  return (
    <Vyber className="field" value={polozkaId || ""} data-testid={`${testid}-vyber`}
      onChange={(e) => { const z = seznam.find((x) => x.id === e.target.value); onChange(z || null); }}>
      <option value="">Vyber model…</option>
      {seznam.map((z) => <option key={z.id} value={z.id}>{z.name}{z.katalogove_cislo ? ` · ${z.katalogove_cislo}` : ""}</option>)}
    </Vyber>
  );
}
